import styled, { css } from 'styled-components';

import blockOne from '@r1-frontend/ui-react/assets/img/icons/throbber/block-one.svg';
import blockThree from '@r1-frontend/ui-react/assets/img/icons/throbber/block-three.svg';
import blockTwo from '@r1-frontend/ui-react/assets/img/icons/throbber/block-two.svg';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import { TBlockAnimation } from '.';

interface IStyleProps {
    hide?: boolean,
    position?: string,
    size?: number,
    withoutTop?: boolean,
}

interface IBlockProps {
    bgColor?: string,
    isLoading?: boolean,
    animation?: TBlockAnimation,
}

interface IBlockConfig {
    maskImage: string,
    left?: number,
    animation: string,
}

const translateStyle = ({ isLoading }: IBlockProps) =>
    [
        'translateX(0px)',
        `scale(${isLoading ? 1 : 1.2})`,
        `translateY(${isLoading ? -7 : -14}px)`,
    ].join(' ');

const createBlock = (
    { bgColor }: IBlockProps,
    { maskImage, left = 0, animation }: IBlockConfig,
) => `
    background-color: ${bgColor || COLORS.ButtonAccent};
    mask-image: url(${maskImage});
    mask-position: center;
    mask-repeat: no-repeat;
    width: 18px;
    height: 18px;
    animation: ${animation} 1.1s infinite;
    transform-box: fill-box;
    transform-origin: 50% 50%;
    left: ${left}px;
    opacity: 0.2;
`;

export const Wrapper = styled.div<IStyleProps>`
    display: ${props => props.hide ? 'none' : 'flex'};
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;
    position: ${props => props.position || 'relative'};
    z-index: 100;
    grid-gap: 10px;
    ${props => props.withoutTop ? undefined : css`top: 50%;`}
    width: 100%;
    right: 0;
    transform: scale(${props => props.size});
`;

/**
 * Генератор идентификаторов анимации блоков троббера,
 * чтобы удобнее было вынести конфигурацию анимируемых блоков в один метод
 * @notes
 * При каждом вызове функции получаем зацикленную секвенцию из идентификаторов от 1 до 3.
 * Когда идентификатор достигает значения 3 - счетчик сбрасывается и начинается заново
 */
const createAnimationId = (function() {
    let value = 0;

    return function() {
        if (value === 3) {
            value = 1;
        } else {
            value++;
        }

        return value;
    };
})();

const configureBlock = (props: IBlockProps, config: Omit<IBlockConfig, 'animation'>) => {
    const animationId = createAnimationId();
    const animation = `${props.animation?.name || 'Block'}_${animationId}`;
    const block = createBlock(props, { ...config, animation });

    return { animation, block };
};

export const FirstBlock = styled.div<IBlockProps>`
    ${p => {
        const { animation, block } = configureBlock(p, { maskImage: blockOne });

        return css`
            ${block}
            @keyframes ${animation} {
                0% {
                    transform: translateX(0px) translateY(0px);
                    opacity: 0.2;
                }
                27.27% {
                    transform: ${translateStyle};
                    opacity: 1;
                }
                54.55% {
                    opacity: 0.2;
                }
            }
        `;
    }}
`;

export const SecondBlock = styled.div<IBlockProps>`
    ${p => {
        const { animation, block } = configureBlock(p, { maskImage: blockTwo });

        return css`
            ${block}
            @keyframes ${animation} {
                27.27% {
                    transform: translateX(0px) translateY(0px);
                    opacity: 0.2;
                }
                54.55% {
                    transform: ${translateStyle};
                    opacity: 1;
                }
                81.82% {
                    opacity: 0.2;
                }
            }
        `;
    }}
`;

export const ThirdBlock = styled.div<IBlockProps>`
    ${p => {
        const { animation, block } = configureBlock(p, { maskImage: blockThree });

        return css`
            ${block}
            @keyframes ${animation} {
                100% {
                    opacity: 0.2;
                }
                45.45% {
                    transform: translateX(0px) translateY(0px);
                    opacity: 0.2;
                }
                72.73% {
                    transform: ${translateStyle};
                    opacity: 1;
                }
            }
        `;
    }}
`;
